import { Box, Stack, Typography } from "@mui/material";
import { OrionIconButton } from "@orion/OrionComponents/OrionIconButton";
import { useState } from "react";

import { type UploadItem } from "../uploadManager";
import { useUploads } from "../useUploads";
import { UploadProgressRow } from "./UploadProgressRow";

import { ExpandLessIcon, ExpandMoreIcon } from "@/icons";

const ROW_HEIGHT_PX = 56;
const VISIBLE_ROWS = 3;

const buildHeaderLabel = (items: UploadItem[]): string => {
  const uploading = items.filter((i) => i.status === "uploading").length;
  const queued = items.filter((i) => i.status === "queued").length;
  const failed = items.filter((i) => i.status === "failed").length;
  const total = items.length;

  // Show "Uploading X of Y" while anything is still in motion (uploading OR
  // queued). X is the count actively transferring; queued items appear as
  // "Waiting…" rows below.
  if (uploading > 0 || queued > 0) {
    return `Uploading ${uploading} of ${total}`;
  }
  if (failed > 0) {
    return `Uploading complete with ${failed} ${failed === 1 ? "error" : "errors"}`;
  }
  return "Done";
};

export function UploadProgressOverlay() {
  const { items, cancel } = useUploads();
  const [minimized, setMinimized] = useState(false);

  if (items.length === 0) return null;

  const header = buildHeaderLabel(items);

  return (
    <Box
      role="region"
      aria-label="Upload progress"
      sx={{
        position: "fixed",
        bottom: 24,
        right: 24,
        width: 360,
        zIndex: (theme) => theme.zIndex.snackbar,
        bgcolor: "background.paper",
        boxShadow: 6,
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          px: 3,
          py: 2,
          bgcolor: "primary.dark",
          color: "primary.contrastText",
        }}
      >
        <Typography
          variant="subtitle2"
          data-testid="upload-progress-header"
          sx={{ flex: 1 }}
        >
          {header}
        </Typography>
        <OrionIconButton
          aria-label={
            minimized ? "Expand upload progress" : "Minimize upload progress"
          }
          onClick={() => setMinimized((m) => !m)}
          size="small"
          sx={{ color: "inherit" }}
        >
          {minimized ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </OrionIconButton>
      </Stack>
      {!minimized && (
        <Box
          sx={{
            maxHeight: ROW_HEIGHT_PX * VISIBLE_ROWS,
            overflowY: "auto",
          }}
        >
          {items.map((item) => (
            <UploadProgressRow key={item.id} item={item} onCancel={cancel} />
          ))}
        </Box>
      )}
    </Box>
  );
}
